import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    History,
    Download,
    Search,
    Filter,
    Plus,
    Edit,
    Check,
    X,
    MessageSquare,
    FileText,
    Clock,
    TrendingUp,
    AlertTriangle,
    CheckCircle
} from 'lucide-react';

const activityTypes = [
    { value: 'created', label: 'Création', icon: Plus, color: 'bg-green-100 text-green-800' },
    { value: 'modified', label: 'Modification', icon: Edit, color: 'bg-blue-100 text-blue-800' },
    { value: 'approved', label: 'Approbation', icon: Check, color: 'bg-teal-100 text-teal-800' },
    { value: 'rejected', label: 'Rejet', icon: X, color: 'bg-red-100 text-red-800' },
    { value: 'commented', label: 'Commentaire', icon: MessageSquare, color: 'bg-purple-100 text-purple-800' },
    { value: 'document_added', label: 'Document ajouté', icon: FileText, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'milestone', label: 'Jalon', icon: CheckCircle, color: 'bg-orange-100 text-orange-800' }
];

const projectHistory = [
    {
        id: 1,
        type: 'created',
        user: 'Marie Rakoto',
        userAvatar: 'MR',
        timestamp: '2025-01-15T09:00:00',
        description: 'Projet créé avec un budget initial de 1 000 000€',
        details: {
            field: 'Projet',
            oldValue: null,
            newValue: 'Expansion Ligne Yaourts - Phase 2'
        },
        impact: 'high'
    },
    {
        id: 2,
        type: 'modified',
        user: 'Sophie Ranaivo',
        userAvatar: 'SR',
        timestamp: '2025-01-16T14:30:00',
        description: 'Budget mis à jour suite à l\'analyse préliminaire',
        details: {
            field: 'total_budget',
            oldValue: '1 000 000€',
            newValue: '1 150 000€'
        },
        impact: 'high'
    },
    {
        id: 3,
        type: 'document_added',
        user: 'Paul Andriamahefa',
        userAvatar: 'PA',
        timestamp: '2025-01-17T11:15:00',
        description: 'Cahier des charges technique ajouté',
        details: {
            field: 'Documents',
            oldValue: null,
            newValue: 'CDC_Technique_v1.pdf'
        },
        impact: 'medium'
    },
    {
        id: 4,
        type: 'approved',
        user: 'Jean Rakotoarison',
        userAvatar: 'JR',
        timestamp: '2025-01-18T16:45:00',
        description: 'Validation métier approuvée avec ROI estimé à 18.5%',
        details: {
            field: 'validation_status',
            oldValue: 'pending',
            newValue: 'approved'
        },
        impact: 'high'
    },
    {
        id: 5,
        type: 'commented',
        user: 'Marie Rakoto',
        userAvatar: 'MR',
        timestamp: '2025-01-19T10:20:00',
        description: 'Commentaire ajouté sur l\'avancement des appels d\'offres',
        details: {
            field: 'Commentaires',
            oldValue: null,
            newValue: 'Mise à jour appels d\'offres équipements'
        },
        impact: 'low'
    },
    {
        id: 6,
        type: 'milestone',
        user: 'Système',
        userAvatar: 'SY',
        timestamp: '2025-01-19T17:00:00',
        description: 'Jalon "Validation technique" atteint',
        details: {
            field: 'Progression',
            oldValue: '15%',
            newValue: '25%'
        },
        impact: 'medium'
    },
    {
        id: 7,
        type: 'modified',
        user: 'Paul Andry',
        userAvatar: 'PA',
        timestamp: '2025-01-20T09:30:00',
        description: 'Date de fin estimée mise à jour',
        details: {
            field: 'end_date',
            oldValue: '2025-08-15',
            newValue: '2025-08-30'
        },
        impact: 'medium'
    },
    {
        id: 8,
        type: 'rejected',
        user: 'Direction Technique',
        userAvatar: 'DT',
        timestamp: '2025-01-20T13:15:00',
        description: 'Proposition fournisseur XYZ rejetée pour non-conformité',
        details: {
            field: 'Fournisseur',
            oldValue: 'XYZ Equipment',
            newValue: null
        },
        impact: 'medium'
    }
];

const typeConfig = activityTypes.reduce((acc, type) => {
    acc[type.value] = type;
    return acc;
}, {});

const impactColors = {
    high: 'border-l-red-500 bg-red-50',
    medium: 'border-l-yellow-500 bg-yellow-50',
    low: 'border-l-green-500 bg-green-50'
};

// @ts-nocheck
export default function HistoryTab({ project }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterUser, setFilterUser] = useState('all');

    const uniqueUsers = [...new Set(projectHistory.map(h => h.user))];

    const filteredHistory = projectHistory.filter(activity => {
        const matchesSearch = activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            activity.user.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || activity.type === filterType;
        const matchesUser = filterUser === 'all' || activity.user === filterUser;
        return matchesSearch && matchesType && matchesUser;
    });

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActivityStats = () => {
        const total = projectHistory.length;
        const byType = activityTypes.map(type => ({
            ...type,
            count: projectHistory.filter(h => h.type === type.value).length
        }));
        const byImpact = {
            high: projectHistory.filter(h => h.impact === 'high').length,
            medium: projectHistory.filter(h => h.impact === 'medium').length,
            low: projectHistory.filter(h => h.impact === 'low').length
        };
        return { total, byType, byImpact };
    };

    const stats = getActivityStats();

    return (
        <div className="space-y-6">
            {/* History Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-teal-600 mb-1">Activités totales</p>
                                <p className="text-3xl font-bold text-teal-900">{stats.total}</p>
                            </div>
                            <div className="w-12 h-12 bg-teal-200 rounded-2xl flex items-center justify-center">
                                <History className="w-6 h-6 text-teal-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-red-600 mb-1">Impact élevé</p>
                                <p className="text-3xl font-bold text-red-900">{stats.byImpact.high}</p>
                                <p className="text-sm text-red-600 mt-1">changements majeurs</p>
                            </div>
                            <div className="w-12 h-12 bg-red-200 rounded-2xl flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600 mb-1">Contributeurs</p>
                                <p className="text-3xl font-bold text-blue-900">{uniqueUsers.length}</p>
                                <p className="text-sm text-blue-600 mt-1">utilisateurs actifs</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-200 rounded-2xl flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-blue-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600 mb-1">Dernière activité</p>
                                <p className="text-lg font-bold text-green-900">Aujourd'hui</p>
                                <p className="text-sm text-green-600 mt-1">13:15</p>
                            </div>
                            <div className="w-12 h-12 bg-green-200 rounded-2xl flex items-center justify-center">
                                <Clock className="w-6 h-6 text-green-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters and Search */}
            <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                placeholder="Rechercher dans l'historique..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Filtrer par type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types</SelectItem>
                                {activityTypes.map(type => (
                                    <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterUser} onValueChange={setFilterUser}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Filtrer par utilisateur" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les utilisateurs</SelectItem>
                                {uniqueUsers.map(user => (
                                    <SelectItem key={user} value={user}>
                                        {user}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button variant="outline" className="gap-2">
                            <Download className="w-4 h-4" />
                            Exporter PDF
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Activity Timeline */}
            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-bold text-gray-900">Timeline des activités</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {filteredHistory.map((activity, index) => {
                            const typeInfo = typeConfig[activity.type];
                            const Icon = typeInfo?.icon || Clock;

                            return (
                                <div
                                    key={activity.id}
                                    className={`border-l-4 pl-6 py-4 ${impactColors[activity.impact]} rounded-r-lg`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="relative">
                                            <Avatar className="w-10 h-10">
                                                <AvatarFallback className="bg-teal-600 text-white text-xs">
                                                    {activity.userAvatar}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ${typeInfo?.color} flex items-center justify-center`}>
                                                <Icon className="w-3 h-3" />
                                            </div>
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <span className="font-semibold text-gray-900">{activity.user}</span>
                                                <Badge className={typeInfo?.color}>
                                                    {typeInfo?.label}
                                                </Badge>
                                                <span className="text-sm text-gray-500">
                          {formatTimestamp(activity.timestamp)}
                        </span>
                                                <Badge
                                                    variant="outline"
                                                    className={`text-xs ${
                                                        activity.impact === 'high' ? 'border-red-300 text-red-700' :
                                                            activity.impact === 'medium' ? 'border-yellow-300 text-yellow-700' :
                                                                'border-green-300 text-green-700'
                                                    }`}
                                                >
                                                    Impact {activity.impact === 'high' ? 'élevé' : activity.impact === 'medium' ? 'moyen' : 'faible'}
                                                </Badge>
                                            </div>

                                            <p className="text-gray-700 mb-3">{activity.description}</p>

                                            {activity.details.oldValue !== null && (
                                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                                    <div className="text-sm">
                                                        <span className="font-medium text-gray-700">Champ modifié: </span>
                                                        <span className="text-gray-900">{activity.details.field}</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                                                        <div>
                                                            <span className="text-gray-500">Ancienne valeur:</span>
                                                            <div className="mt-1 p-2 bg-red-50 rounded border border-red-200 line-through text-red-700">
                                                                {activity.details.oldValue || 'Vide'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Nouvelle valeur:</span>
                                                            <div className="mt-1 p-2 bg-green-50 rounded border border-green-200 text-green-700">
                                                                {activity.details.newValue || 'Supprimé'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {filteredHistory.length === 0 && (
                        <div className="text-center py-12">
                            <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">Aucune activité trouvée pour les filtres actuels</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
